import type { MembershipRole } from './seat'
import { roleLabel } from './role-label'

export const CLUB_FEATURES_DISABLED_CODE = 'CLUB_FEATURES_DISABLED'

export const CLUB_FEATURES_SETTING_LABEL = 'Private Members'

export const CLUB_FEATURES_SETTING_LOCATION = 'Settings → Outfitter Details'

export function isClubFeaturesRefusal(code: string | null | undefined): boolean {
  return code === CLUB_FEATURES_DISABLED_CODE
}

export const CLUB_FEATURES_GATED_ROLES: readonly MembershipRole[] = ['member', 'guest']

export function isClubFeaturesGatedRole(role: MembershipRole): boolean {
  return CLUB_FEATURES_GATED_ROLES.includes(role)
}

export function clubFeaturesGatedRoleLabels(roles: readonly string[]): string[] {
  const labels: string[] = []
  for (const role of roles) {
    const label = roleLabel(role as MembershipRole, 'outfitter')
    if (labels.includes(label)) continue
    labels.push(label)
  }
  return labels
}

export function describeClubFeaturesGatedRoles(roles: readonly string[]): string {
  const labels = clubFeaturesGatedRoleLabels(roles)
  if (labels.length === 0) return 'That role'
  if (labels.length === 1) return `The ${labels[0]} role`
  const head = labels.slice(0, -1).join(', ')
  return `The ${head} and ${labels[labels.length - 1]} roles`
}

export function clubFeaturesGatedRolesAreSingular(roles: readonly string[]): boolean {
  return clubFeaturesGatedRoleLabels(roles).length <= 1
}

export function clubFeaturesRequiredMessage(roles: readonly string[]): string {
  const subject = describeClubFeaturesGatedRoles(roles)
  const verb = clubFeaturesGatedRolesAreSingular(roles) ? 'needs' : 'need'
  return (
    `${subject} ${verb} ${CLUB_FEATURES_SETTING_LABEL} turned on for this outfitter. ` +
    `An outfitter owner can enable it in ${CLUB_FEATURES_SETTING_LOCATION}.`
  )
}

export const CLUB_FEATURES_MEMBER_UNAVAILABLE_LEAD =
  `Members are unavailable while ${CLUB_FEATURES_SETTING_LABEL} is off.`

export type ClubFeaturesMemberSurface = 'booking-party' | 'seat-role'

const CLUB_FEATURES_MEMBER_REMEDIATION: Record<ClubFeaturesMemberSurface, string> = {
  'booking-party': `Turn it on under ${CLUB_FEATURES_SETTING_LOCATION} to offer members in a booking party.`,
  'seat-role': `Turn it on under ${CLUB_FEATURES_SETTING_LOCATION} to assign the Member role.`,
}

const CLUB_FEATURES_OWNER_ONLY = 'Only the outfitter owner can change this.'

export function clubFeaturesMemberUnavailableMessage(
  canManage: boolean,
  surface: ClubFeaturesMemberSurface = 'booking-party',
): string {
  const tail = canManage
    ? CLUB_FEATURES_MEMBER_REMEDIATION[surface]
    : CLUB_FEATURES_OWNER_ONLY
  return `${CLUB_FEATURES_MEMBER_UNAVAILABLE_LEAD} ${tail}`
}

export const CLUB_FEATURES_MEMBER_UNAVAILABLE_MESSAGE =
  clubFeaturesMemberUnavailableMessage(true)

export function clubFeaturesGatedRolesUnavailableMessage(
  roles: readonly string[],
  canManage: boolean,
): string {
  const subject = describeClubFeaturesGatedRoles(roles)
  const isSingular = clubFeaturesGatedRolesAreSingular(roles)
  const verb = isSingular ? 'is' : 'are'
  const pronoun = isSingular ? 'it' : 'them'
  const lead = `${subject} ${verb} unavailable while ${CLUB_FEATURES_SETTING_LABEL} is off.`
  const tail = canManage
    ? `Turn it on under ${CLUB_FEATURES_SETTING_LOCATION} to assign ${pronoun}.`
    : CLUB_FEATURES_OWNER_ONLY
  return `${lead} ${tail}`
}

export function clubFeaturesGrandfatheredHuntsHeading(count: number): string {
  const subject = count === 1 ? '1 hunt was' : `${count} hunts were`
  return `${subject} scheduled before ${CLUB_FEATURES_SETTING_LABEL} was turned off.`
}

export const CLUB_FEATURES_GRANDFATHERED_HUNTS_LEAD =
  'They stay on the calendar so you can cancel them, but they cannot be edited and no new hunts can be scheduled.'

const CLUB_FEATURES_GRANDFATHERED_HUNTS_REMEDIATION =
  `Turn ${CLUB_FEATURES_SETTING_LABEL} back on under ${CLUB_FEATURES_SETTING_LOCATION} to schedule hunts again.`

export function clubFeaturesGrandfatheredHuntsMessage(canManage: boolean): string {
  const tail = canManage
    ? CLUB_FEATURES_GRANDFATHERED_HUNTS_REMEDIATION
    : CLUB_FEATURES_OWNER_ONLY
  return `${CLUB_FEATURES_GRANDFATHERED_HUNTS_LEAD} ${tail}`
}
